import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet"; // ✅ This is the key addition
import styled, { createGlobalStyle } from "styled-components";
import SealCharts from "./SealCharts";


const GlobalStyle = createGlobalStyle`
  @keyframes panTexture {
    from {
      background-position: 0 0;
    }
    to {
      background-position: -2048px -1024px;
    }
  }

  svg.duotone-filters {
    position: absolute;
    width: 0;
    height: 0;
  }
`;


const FlipperFrenzy = () => {
  const [scrollPos, setScrollPos] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollPos(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
<>
<Helmet>
  <title>Flipper Frenzy | Seal Racing Chaos by RolandStudios</title>
  <meta
    name="description"
    content="Flop, boost, and belly-slide through multiplayer mayhem in Flipper Frenzy — the wild seal racing game by solo dev RolandStudios."
  />
  <meta
    name="keywords"
    content="Flipper Frenzy, seal racing game, physics kart game, indie multiplayer, RolandStudios, surreal racing game, couch chaos"
  />
  <meta property="og:title" content="Flipper Frenzy | RolandStudios" />
  <meta
    property="og:description"
    content="Flipper Frenzy is the most chaotic seal racing game you’ve never asked for—flop, slide, and bonk your way to victory."
  />
  <meta property="og:image" content="/images/LibraryLogo.png" />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://www.rolandstudios.com/flipperfrenzy" />

  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Flipper Frenzy by RolandStudios" />
  <meta
    name="twitter:description"
    content="Flop. Slide. Win. A chaotic multiplayer seal racing game like no other."
  />
  <meta name="twitter:image" content="/images/LibraryLogo.png" />
</Helmet>

  <GlobalStyle />
  <svg style={{ display: 'none' }}>
  <filter id="sealFurDuotoneBlue" colorInterpolationFilters="sRGB">
  <feColorMatrix
      type="matrix"
      values="
        1 0 0 0 0
        0 1 0 0 0
        0 0 1 0 0
        0 0 0 1 0
      "
    />
<feComponentTransfer>
  <feFuncR type="table" tableValues="0.72 0.84" />
  <feFuncG type="table" tableValues="0.85 0.92" />
  <feFuncB type="table" tableValues="0.93 0.99" />
</feComponentTransfer>


  </filter>
</svg>

      <Container>
        <Background>
        <FurOverlay scrollPos={scrollPos} />

          <StyledVideo
            src="/videos/flipperfrenzy.mp4"
            autoPlay
            loop
            muted
            scrollPos={scrollPos}
          />
        </Background>

        <MaxWidthContainer>
        <LogoSection>
  <Logo src="/images/LibraryLogo.png" alt="Flipper Frenzy Logo" scrollPos={scrollPos} />
</LogoSection>


          <IcePanel scrollPos={scrollPos}>
            <Columns>
              <Left>
                <SealCharts />
              </Left>
              <Right>
                <h1>Basically a racing game with seals</h1>
                <Text>
                  <p>
                  Flipper Frenzy is what happens when you take a Kart Klone,
                  throw it in the ocean, and replace everyone with floppy,
                  overenthusiastic sea blobs. Flop. Slide. Win. The most chaotic 
                  seal racing game you've never asked for—until now.
                  </p>
                  <DiscordLink
                    href="https://discord.gg/H8BABPTstx"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src="/images/discord.png" alt="Join our Discord" />
                    <span>Join our Discord</span>
                  </DiscordLink>

                  <p>Things you can expect:</p>
                  <ul>
                    <li>💥 Hilarious physics-based flopping mayhem</li>
                    <li>🧼 Clean races? Nope. Expect bumps, bounces, and belly-slides</li>
                    <li>🛝 Custom seal vibes—from pastel to Sealberpunk 2077</li>
                    <li>👯‍♀️ Couch chaos (split-screen), solo grind, and online duels</li>
                    <li>🧠 No plot. Just vibes.</li>
                  </ul>

                  <p>
                    We didn’t set out to win awards. We set out to make a game
                    where seals bonk into stuff and somehow win races.
                  </p>

                  <p>The Flipper Frenzy code of honor:</p>
                  <ul>
                    <li>No battle passes 🙅‍♂️</li>
                    <li>No skins locked behind 400-tier grinds ❌</li>
                    <li>No time-limited FOMO bait 🕑</li>
                    <li>Buy once. Play forever. Fall off a cliff. Laugh. Repeat.</li>
                  </ul>

                  <p>It’s not just a party game. It’s chaos in the ocean.</p>
                </Text>

                <Section reverse>
                  <Video>
                  <iframe src="https://player.vimeo.com/video/1041220855?controls=0&autoplay=1&playsinline=1&color&autopause=0&loop=1&muted=1&title=1&portrait=1&byline=1#t=">
    
    </iframe>
                  </Video>
                  <Text>
                    <p>Master the art of the galumph. Win—or flop—gracefully.</p>
                  </Text>
                </Section>

                <Section>
                  <Text>
                    <p>Build your dream seal, then dive into chaos:</p>
                    <ul>
                      <li>Sky Tubes 🌈</li>
                      <li>Frozen Glaciers ❄️</li>
                      <li>Seal Dystopia 🤖</li>
                    </ul>
                  </Text>
                  <Video>
                  <iframe src="https://player.vimeo.com/video/1081365251?controls=0&autoplay=1&playsinline=1&color&autopause=0&loop=1&muted=1&title=1&portrait=1&byline=1#t=">
    
    </iframe>
                  </Video>
                </Section>

                <Section reverse>
                  <Video>
                  <iframe src="https://player.vimeo.com/video/1081372870?controls=0&autoplay=1&playsinline=1&color&autopause=0&loop=1&muted=1&title=1&portrait=1&byline=1#t=">
    
    </iframe>
                  </Video>
                  <Text>
                    <p>
                      Every level is crafted by hand. No lazy lava clones here—
                      just wild ideas and unpredictable terrain.
                    </p>
                  </Text>
                </Section>

                <Section>
                  <Text>
                    <p>Coming soon in updates:</p>
                    <ul>
                      <li>Leopard Seals 🐆</li>
                      <li>Weddell Seals ❄️</li>
                      <li>Elephant Seals 🐘</li>
                      <li>Sea Lions 🦁 (but wet)</li>
                    </ul>
                  </Text>
                  <Video>
                  <iframe src="https://player.vimeo.com/video/1081371820?controls=0&autoplay=1&playsinline=1&color&autopause=0&loop=1&muted=1&title=1&portrait=1&byline=1#t=">
    
    </iframe>
                  </Video>
                </Section>
              </Right>
            </Columns>
          </IcePanel>
        </MaxWidthContainer>
        <MobileScrollSpacer />
      </Container>
    </>
  );
};

export default FlipperFrenzy;

// --- Styled Components ---

const Container = styled.div`
  width: 100vw;
  min-height: 100vh;
  overflow-x: hidden;
  position: relative;
  z-index: 0;
`;

const Background = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -2;
  overflow: hidden;
`;

const FurOverlay = ({ scrollPos }) => {
  const opacity = Math.min(Math.max((scrollPos - 100) / 400, 0), 1);
  const blur = Math.max(14 - scrollPos / 20, 0); // start blurry, clear as you scroll

  return (
    <FurOverlayStyled
      style={{
        opacity,
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`
      }}
    />
  );
};



// 👇 Then your styled div as it is:
const FurOverlayStyled = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image:  url("/images/RingTexture.png");
  background-repeat: repeat;
  background-size: 1024px;
  background-position: 0 0;
  pointer-events: none;
  z-index: 1;

  animation: panTexture 120s linear infinite;
  filter: url(#sealFurDuotoneBlue);

`;



const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  position: absolute;
  z-index: -1;

  ${({ scrollPos }) => scrollPos > 700 && `
    display: none;
  `}

  filter: ${({ scrollPos }) => `
    blur(${Math.min(scrollPos / 5, 30)}px)
    brightness(${Math.min(Math.max(100 + scrollPos / 5, 40), 120)}%)
  `};
`;

const MaxWidthContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 60px 20px 200px;
`;

const MobileScrollSpacer = styled.div`
  height: 160px;
  width: 100%;
  display: none;

  @media (max-width: 800px) {
    display: block;
  }
`;

const LogoSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 150px;
  margin-bottom: 200px;
`;

const Logo = styled.img`
  width: 800px;
  height: auto;
  max-width: 80%;
  filter: 
    brightness(1.1)
    drop-shadow(0 0 20px rgba(255, 255, 255, 0.24));
  transition: transform 0.2s ease, opacity 0.3s ease;

  opacity: ${({ scrollPos }) => Math.max(1 - scrollPos / 250, 0)};
  transform: ${({ scrollPos }) => `translateY(${scrollPos * 0.3}px)`};
`;



const IcePanel = styled.div`
  border-radius: 30px;
  background: rgba(200, 230, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(14px);
  padding: 40px;
  box-shadow: 0 4px 30px rgba(34, 78, 78, 0.56);
  margin-bottom: 60px;
  transition: all 0.3s ease;

  opacity: ${({ scrollPos }) => Math.min(Math.max(scrollPos / 200, 0.4), 1)};

  li,
  p {
    font-family: 'Caveat Brush', cursive;
  }

  h1 {
    color: rgb(255, 249, 87);
    font-size: 40px;
    font-weight: bold;
    font-family: 'Caveat Brush', cursive;
    text-shadow: 4px 4px 8px rgba(0, 0, 0, 0.6);
  }

  @media (max-width: 800px) {
    padding: 20px;
  }
`;


const Columns = styled.div`
  display: flex;
  gap: 40px;
  justify-content: space-between;

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const Left = styled.div`
  flex: 1;
  max-width: 100%;
  overflow-y: hidden; /* 👈 NEW */

  @media (max-width: 800px) {
    max-width: 100%;
  }
`;

const Right = styled.div`
  flex: 1;
  padding: 20px;
  max-width: 50%;
  font-family: 'Caveat Brush', cursive;

  @media (max-width: 800px) {
    max-width: 100%;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: ${({ reverse }) => (reverse ? "row-reverse" : "row")};
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const Video = styled.div`
  flex: 1;

  iframe {
    width: 100%;
    max-width: 400px;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    border: 2px solid rgba(255, 255, 255, 0.25);
    box-shadow: 4px 4px 12px rgba(0, 0, 0, 0.5);
  }
`;

const Text = styled.div`
  flex: 1;
  font-size: 1.4em;
  line-height: 1.6;
  color: white;
  font-family: 'Caveat Brush', cursive;
  text-shadow: 1px 1px 3px black;
`;

const DiscordLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 30px;
  padding: 18px;
  width: 100%;
  border-radius: 16px;
  background: rgba(88, 101, 242, 0.2);
  border: 2px solid rgba(88, 101, 242, 0.4);
  text-decoration: none;
  color: white;
  font-family: 'Caveat Brush', cursive;
  font-size: 22px;
  text-shadow: 1px 1px 3px black;
  transition: all 0.3s ease;
  box-shadow: 0 0 12px rgba(88, 101, 242, 0.15);

  img {
    width: 34px;
    height: 28px;
    filter: invert(67%) sepia(82%) saturate(610%) hue-rotate(210deg)
      brightness(150%) contrast(97%);
  }

  &:hover {
    background: rgba(88, 101, 242, 0.4);
    box-shadow: 0 0 20px rgba(88, 101, 242, 0.6);
  }
`;